import { FC } from "react";
import { FieldInputProps } from "react-final-form";
import { useSelector } from "react-redux";

import { Error } from "@livechat/design-system-icons";
import { IPickerListItem, Icon, Picker } from "@livechat/design-system-react-components";

import { AgentPickerListItem, AgentPickerSelectedListItem } from "components/picker-items/agentPickerItems";
import { shortPickerStyles } from "routes/automations/shared/styles";
import {
  IAgent,
  IWithAgentsState,
  selectAgents,
  selectAgentsAlphabetically,
  selectLoggedInAgent,
} from "store/entities/agents";
import { IRuleTriggerAgentID, IRuleTriggerHasAssignee } from "store/entities/rules/interfaces";
import { IWithSessionState } from "store/features/session";

import { changeTrigger, changeTriggerValues } from "../utils";

interface IProps {
  input: FieldInputProps<IRuleTriggerAgentID | IRuleTriggerHasAssignee>;
}

type EqualityType = "is" | "isNot" | "isAny" | "isNone";

export const AgentTrigger: FC<IProps> = ({ input }) => {
  const agentsById = useSelector(selectAgents);
  const loggedInAgent = useSelector(selectLoggedInAgent);
  const agents = useSelector((state: IWithAgentsState & IWithSessionState) =>
    selectAgentsAlphabetically(state, {
      activeOnly: true,
      loggedInAgentOnTop: true,
      excludeViewers: true,
    })
  );

  const equalityOptions = [
    createEqualityOption("is"),
    createEqualityOption("isNot"),
    createEqualityOption("isAny"),
    createEqualityOption("isNone"),
  ];

  const selectedEqualityOptions = [createEqualityOption(getEqualityOption(input.value))];

  const agentOptions = agents.map(agent => createAgentOption(agent, loggedInAgent));

  const selectedAgents: IPickerListItem[] =
    input.value.type === "agentID"
      ? input.value.in.map(id => {
          const agent = agentsById[id];
          return agent ? createAgentOption(agent, loggedInAgent) : createRemovedAgentOption(id);
        })
      : [];

  const shouldDisplayAgentPicker = input.value.type === "agentID";

  const handleSelectEqualityOption = (selectedOptions: IPickerListItem[] | null): void => {
    if (!selectedOptions || selectedOptions.length === 0) {
      return;
    }

    const selectedOption = selectedOptions[0];

    const selectedAgentIds = selectedAgents.map(agent => agent.key);

    switch (selectedOption.key) {
      case "is":
        changeTrigger(input, { type: "agentID", in: selectedAgentIds, invert: false });
        break;
      case "isNot":
        changeTrigger(input, { type: "agentID", in: selectedAgentIds, invert: true });
        break;
      case "isAny":
        changeTrigger(input, { type: "hasAssignee", eq: true });
        break;
      case "isNone":
        changeTrigger(input, { type: "hasAssignee", eq: false });
        break;
    }
  };

  const handleSelectAgent = (selectedOptions: IPickerListItem[] | null): void => {
    if (!selectedOptions || selectedOptions.length === 0) {
      changeTriggerValues(input, { in: [] });
      return;
    }

    changeTriggerValues(input, { in: selectedOptions.map(option => option.key) });
  };

  return (
    <>
      <Picker
        options={equalityOptions}
        selected={selectedEqualityOptions}
        onSelect={handleSelectEqualityOption}
        hideClearButton
        searchDisabled
        css={shortPickerStyles}
      />
      {shouldDisplayAgentPicker && (
        <Picker
          type="multi"
          options={agentOptions}
          selected={selectedAgents}
          onSelect={handleSelectAgent}
          placeholder="Select one agent or more…"
        />
      )}
    </>
  );
};

const createEqualityOption = (key: EqualityType): IPickerListItem => {
  switch (key) {
    case "is":
      return { key: "is", name: "is" };
    case "isNot":
      return { key: "isNot", name: "is not" };
    case "isAny":
      return { key: "isAny", name: "is any" };
    case "isNone":
      return { key: "isNone", name: "is none" };
  }
};

const getEqualityOption = (value: IRuleTriggerAgentID | IRuleTriggerHasAssignee): EqualityType => {
  if (value.type === "agentID") {
    return value.invert ? "isNot" : "is";
  }

  return value.eq ? "isAny" : "isNone";
};

const createAgentOption = (agent: IAgent, loggedInAgent: IAgent): IPickerListItem => ({
  key: agent.id,
  name: agent.name,
  customElement: {
    selectedItemBody: <AgentPickerSelectedListItem agentName={agent.name} avatarUrl={agent.avatar} />,
    listItemBody: (
      <AgentPickerListItem
        agentName={agent.name}
        avatarUrl={agent.avatar}
        isLoggedInAgent={agent.id === loggedInAgent.id}
      />
    ),
  },
});

const createRemovedAgentOption = (agentId: string): IPickerListItem => ({
  key: agentId,
  name: "Agent removed",
  selectedTagOptions: {
    kind: "error",
    leftNode: <Icon source={Error} />,
  },
});
